// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/shared/coop-game/engine.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * The pure rules engine of "Ayni: Team Earth"
 * (docs/KIOSK_COOP_GAME_DESIGN.md §4, on votes since docs/KIOSK_GAMES_DESIGN.md §4).
 *
 * `applyCoopCommand` handles one player or operator command, and
 * `tickCoopGame` moves the clock (phase deadlines, idle seats). Both return a
 * new state and never change the one passed in. Time is always passed in
 * (epoch ms), so the main-process host owns the timer and the tests and the
 * balance simulator run instantly.
 *
 * Phases: lobby -> (brief -> plan -> reveal) per round -> finale -> ended.
 *
 * Every decade the team has one pool of tokens. Each present kiosk backs the
 * cards it wants (a ballot that fits the pool); when the plan ends, the cards
 * are played in vote order (`resolveCoopBallots`), and kiosks can come and go
 * at any time.
 */

import { presentSeats, markIdleSeats, markSeatActive } from '../games/presence';
import { seededRandom, shuffled } from '../games/random';
import { countVotes, shouldExtendDeadline } from '../games/vote';
import { COOP_CARDS, COOP_EVENTS, getCoopCard, getCoopEvent } from './content';
import { COOP_MODEL, clampSeverity, decadesPerRound, pathFor, resolveRound, roundYear, starsFor } from './model';
import { severityFromReading } from './readings';
import type { CoopReadings } from './readings';
import { COOP_ROLES } from './types';
import type {
  CoopCommand,
  CoopCommandError,
  CoopCommandResult,
  CoopEndReason,
  CoopGameSettings,
  CoopGameState,
  CoopPath,
  CoopRole,
  CoopTeamTotals
} from './types';

export const COOP_MAX_SEATS = 4;

export const DEFAULT_COOP_GAME_SETTINGS: Readonly<CoopGameSettings> = {
  rounds: 5,
  lobbySeconds: 30,
  briefSeconds: 15,
  planSeconds: 60,
  revealSeconds: 20,
  finaleSeconds: 60,
  idleSeconds: 45,
  autoStart: true
};

function positiveSeconds(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.min(3600, n) : fallback;
}

export function normalizeCoopSettings(partial: Partial<CoopGameSettings> = {}): CoopGameSettings {
  const d = DEFAULT_COOP_GAME_SETTINGS;
  return {
    rounds: partial.rounds === 3 ? 3 : 5,
    lobbySeconds: positiveSeconds(partial.lobbySeconds, d.lobbySeconds),
    briefSeconds: positiveSeconds(partial.briefSeconds, d.briefSeconds),
    planSeconds: partial.planSeconds === null ? null : positiveSeconds(partial.planSeconds, d.planSeconds ?? 60),
    revealSeconds: positiveSeconds(partial.revealSeconds, d.revealSeconds),
    finaleSeconds: positiveSeconds(partial.finaleSeconds, d.finaleSeconds),
    idleSeconds: positiveSeconds(partial.idleSeconds, d.idleSeconds),
    autoStart: partial.autoStart !== false
  };
}

function roleRecord<T>(make: (role: CoopRole) => T): Record<CoopRole, T> {
  return { air: make('air'), climate: make('climate'), water: make('water'), fire: make('fire') };
}

function emptyTotals(): CoopTeamTotals {
  return { cardsPlayed: 0, bridgeCards: 0, backedByAll: 0, sharedRoundCards: 0, votesByCard: {} };
}

/** The lens a kiosk opens on: its slice's system. */
export function defaultLens(sliceIndex: number): CoopRole {
  return COOP_ROLES[((sliceIndex % COOP_ROLES.length) + COOP_ROLES.length) % COOP_ROLES.length] ?? 'air';
}

/** The team's tokens for one round (a 3-round game covers 5/3 decades per round). */
export function coopPoolForRound(rounds: 3 | 5): number {
  return Math.round(COOP_MODEL.teamTokensPerDecade * decadesPerRound(rounds));
}

export interface CreateCoopGameOptions {
  now: number;
  seed?: number;
  settings?: Partial<CoopGameSettings>;
  /** Event strength per system from today's real data (1 = typical, clamped to 0.5..1.5). */
  severity?: Partial<Record<CoopRole, number>>;
  /** Today's readings; each one sets its system's severity (and wins over `severity`). */
  readings?: CoopReadings;
}

export function createCoopGame(options: CreateCoopGameOptions): CoopGameState {
  const settings = normalizeCoopSettings(options.settings);
  const seed = (options.seed ?? options.now) >>> 0;
  return {
    phase: 'lobby',
    endReason: null,
    settings,
    seed,
    eventOrder: shuffled(COOP_EVENTS.map((event) => event.id), seededRandom(seed)).slice(0, settings.rounds),
    severity: roleRecord((role) => {
      const reading = options.readings?.[role];
      return reading ? severityFromReading(role, reading) : clampSeverity(options.severity?.[role] ?? 1);
    }),
    readings: { ...(options.readings ?? {}) },
    round: 0,
    planet: { ...COOP_MODEL.start },
    seats: [],
    pool: coopPoolForRound(settings.rounds),
    planExtended: false,
    totals: emptyTotals(),
    reports: [],
    phaseEndsAt: settings.autoStart ? options.now + settings.lobbySeconds * 1000 : null
  };
}

function clone(state: CoopGameState): CoopGameState {
  return JSON.parse(JSON.stringify(state)) as CoopGameState;
}

const CARD_ORDER = new Map(COOP_CARDS.map((card, index) => [card.id, index]));

/** What a ballot costs. */
export function coopBallotCost(ballot: readonly string[]): number {
  return ballot.reduce((total, id) => total + (getCoopCard(id)?.cost ?? 0), 0);
}

export interface CoopBallotResult {
  /** Card ids played, in the order the vote chose them. */
  cards: string[];
  cost: number;
  votes: Record<string, number>;
  voters: number;
  /** Played cards every voter backed (0 unless there were two voters or more). */
  backedByAll: number;
}

/**
 * The team's plan from the ballots of the kiosks present
 * (docs/KIOSK_GAMES_DESIGN.md §4.3): cards sorted by votes (most first), then
 * cost (cheapest first), then content order; every card that still fits the
 * pool is played. Cards nobody backed are never played. The kiosks' live
 * "if the timer ended now" preview is this same function.
 */
export function resolveCoopBallots(ballots: ReadonlyArray<readonly string[]>, pool: number): CoopBallotResult {
  const votes = countVotes(ballots.map((ballot) => ballot.filter((id) => getCoopCard(id) !== undefined)));
  const voters = ballots.length;
  const order = Object.keys(votes).sort((a, b) =>
    (votes[b] ?? 0) - (votes[a] ?? 0)
    || (getCoopCard(a)?.cost ?? 0) - (getCoopCard(b)?.cost ?? 0)
    || (CARD_ORDER.get(a) ?? 0) - (CARD_ORDER.get(b) ?? 0));
  let left = pool;
  let backedByAll = 0;
  const cards: string[] = [];
  for (const id of order) {
    const cost = getCoopCard(id)?.cost ?? Infinity;
    if (cost > left) continue;
    left -= cost;
    cards.push(id);
    if (voters >= 2 && votes[id] === voters) {
      backedByAll += 1;
      left += COOP_MODEL.consensusRefund;
    }
  }
  return { cards, cost: pool - left, votes, voters, backedByAll };
}

/** The ballots that count right now: those of the present seats. */
export function coopPresentBallots(state: CoopGameState): string[][] {
  return presentSeats(state.seats).map((seat) => [...seat.ballot]);
}

function end(state: CoopGameState, reason: CoopEndReason): void {
  state.phase = 'ended';
  state.endReason = reason;
  state.phaseEndsAt = null;
}

function startGame(state: CoopGameState, now: number): void {
  for (const seat of state.seats) {
    seat.lobbyReady = true;
    seat.lastActiveAt = now;
    seat.away = false;
  }
  state.round = 0;
  beginRound(state, now);
}

function beginRound(state: CoopGameState, now: number): void {
  state.pool = coopPoolForRound(state.settings.rounds);
  state.planExtended = false;
  for (const seat of state.seats) {
    seat.continued = false;
    seat.ready = false;
    seat.ballot = [];
  }
  state.phase = 'brief';
  state.phaseEndsAt = now + state.settings.briefSeconds * 1000;
}

function beginPlan(state: CoopGameState, now: number): void {
  state.phase = 'plan';
  state.phaseEndsAt = state.settings.planSeconds === null ? null : now + state.settings.planSeconds * 1000;
  for (const seat of state.seats) seat.ready = false;
}

function resolvePlan(state: CoopGameState, now: number): void {
  const result = resolveCoopBallots(coopPresentBallots(state), state.pool);
  const cards = result.cards.map((id) => getCoopCard(id)).filter((card) => card !== undefined);
  const event = getCoopEvent(state.eventOrder[state.round] ?? '');
  const before = { ...state.planet };
  const round = resolveRound(before, {
    decades: decadesPerRound(state.settings.rounds),
    event,
    severity: event ? state.severity[event.seededBy] : 1,
    cards
  });

  const totals = state.totals;
  totals.cardsPlayed += cards.length;
  totals.bridgeCards += cards.filter((card) => card.bridge).length;
  if (result.voters >= 2) {
    totals.backedByAll += result.backedByAll;
    totals.sharedRoundCards += cards.length;
  }
  for (const [id, count] of Object.entries(result.votes)) totals.votesByCard[id] = (totals.votesByCard[id] ?? 0) + count;

  state.reports.push({
    round: state.round,
    year: roundYear(state.round, state.settings.rounds),
    eventId: event?.id ?? '',
    before,
    after: round.planet,
    played: result.cards,
    pool: state.pool,
    unspent: Math.max(0, state.pool - result.cost),
    voters: result.voters,
    votes: result.votes,
    backedByAll: result.backedByAll,
    links: round.links
  });
  state.planet = round.planet;
  state.phase = 'reveal';
  state.phaseEndsAt = now + state.settings.revealSeconds * 1000;
  for (const seat of state.seats) seat.continued = false;
}

function beginFinale(state: CoopGameState, now: number): void {
  state.phase = 'finale';
  state.phaseEndsAt = now + state.settings.finaleSeconds * 1000;
}

/** The plan's deadline: extended once when players are here but nobody voted, else resolved. */
function planDeadline(state: CoopGameState, now: number): void {
  const present = presentSeats(state.seats);
  const extend = shouldExtendDeadline({
    presentCount: present.length,
    anyBallots: present.some((seat) => seat.ballot.length > 0),
    alreadyExtended: state.planExtended
  });
  if (extend) {
    state.planExtended = true;
    state.phaseEndsAt = now + COOP_MODEL.planExtensionSeconds * 1000;
    return;
  }
  resolvePlan(state, now);
}

function advance(state: CoopGameState, now: number, byDeadline: boolean): void {
  switch (state.phase) {
    case 'lobby':
      if (state.seats.length === 0) end(state, 'empty-lobby');
      else startGame(state, now);
      return;
    case 'brief':
      beginPlan(state, now);
      return;
    case 'plan':
      if (byDeadline) planDeadline(state, now);
      else resolvePlan(state, now);
      return;
    case 'reveal':
      if (state.round + 1 >= state.settings.rounds) {
        beginFinale(state, now);
      } else {
        state.round += 1;
        beginRound(state, now);
      }
      return;
    case 'finale':
      end(state, 'finished');
      return;
    case 'ended':
      return;
  }
}

/** Moves on early once every present player has tapped through the phase. */
function advanceIfEveryoneDone(state: CoopGameState, now: number): void {
  const present = presentSeats(state.seats);
  if (present.length === 0) return;
  if (state.phase === 'lobby' && state.seats.every((seat) => seat.lobbyReady)) advance(state, now, false);
  else if ((state.phase === 'brief' || state.phase === 'reveal') && present.every((seat) => seat.continued)) advance(state, now, false);
  else if (state.phase === 'plan' && present.every((seat) => seat.ready)) advance(state, now, false);
}

/** Deadlines and idle seats. Call it on a timer (about once a second). */
export function tickCoopGame(input: CoopGameState, now: number): CoopGameState {
  if (input.phase === 'ended') return input;
  const state = clone(input);
  if (state.phase === 'brief' || state.phase === 'plan' || state.phase === 'reveal') {
    markIdleSeats(state.seats, now, state.settings.idleSeconds * 1000);
    if (state.seats.length > 0 && presentSeats(state.seats).length === 0) {
      end(state, 'abandoned');
      return state;
    }
  }
  if (state.phaseEndsAt !== null && now >= state.phaseEndsAt) advance(state, now, true);
  else advanceIfEveryoneDone(state, now);
  return state;
}

/** Newer readings replace older ones and set the severity of events not yet played. */
function applyReadings(state: CoopGameState, readings: CoopReadings): void {
  for (const role of COOP_ROLES) {
    const reading = readings[role];
    if (!reading) continue;
    state.readings[role] = reading;
    state.severity[role] = severityFromReading(role, reading);
  }
}

export function applyCoopCommand(input: CoopGameState, command: CoopCommand, now: number): CoopCommandResult {
  const fail = (error: CoopCommandError): CoopCommandResult => ({ state: input, error });
  if (input.phase === 'ended') return fail('wrong-phase');
  const state = clone(input);

  switch (command.type) {
    case 'start':
      if (state.phase !== 'lobby') return fail('wrong-phase');
      if (state.seats.length === 0) return fail('no-players');
      startGame(state, now);
      return { state };
    case 'stop':
      end(state, 'stopped');
      return { state };
    case 'set_readings':
      applyReadings(state, command.readings);
      return { state };
    case 'skip_to_finale':
      if (state.phase !== 'brief' && state.phase !== 'plan' && state.phase !== 'reveal') return fail('wrong-phase');
      beginFinale(state, now);
      return { state };
    case 'join':
      return join(state, input, command, now);
    default:
      break;
  }

  const seat = state.seats.find((s) => s.seatId === command.seatId);
  if (!seat) return fail('unknown-seat');
  markSeatActive(seat, now);

  switch (command.type) {
    case 'touch':
      break;
    case 'set_language':
      seat.language = command.language;
      break;
    case 'set_lens':
      seat.lens = command.role;
      break;
    case 'leave': {
      state.seats = state.seats.filter((s) => s !== seat);
      if (state.phase !== 'lobby' && state.phase !== 'finale' && state.seats.length === 0) end(state, 'abandoned');
      break;
    }
    case 'lobby_ready':
      if (state.phase !== 'lobby') return fail('wrong-phase');
      seat.lobbyReady = true;
      break;
    case 'continue':
      if (state.phase !== 'brief' && state.phase !== 'reveal') return fail('wrong-phase');
      seat.continued = true;
      break;
    case 'ready':
      if (state.phase !== 'plan') return fail('wrong-phase');
      seat.ready = true;
      break;
    case 'vote_card': {
      if (state.phase !== 'plan') return fail('wrong-phase');
      const card = getCoopCard(command.cardId);
      if (!card) return fail('unknown-card');
      if (seat.ballot.includes(card.id)) {
        seat.ballot = seat.ballot.filter((id) => id !== card.id);
      } else {
        if (coopBallotCost(seat.ballot) + card.cost > state.pool) return fail('ballot-full');
        seat.ballot = [...seat.ballot, card.id];
      }
      seat.ready = false;
      break;
    }
  }

  advanceIfEveryoneDone(state, now);
  return { state };
}

function join(state: CoopGameState, input: CoopGameState, command: Extract<CoopCommand, { type: 'join' }>, now: number): CoopCommandResult {
  const fail = (error: CoopCommandError): CoopCommandResult => ({ state: input, error });
  const existing = state.seats.find((seat) => seat.seatId === command.seatId);
  if (existing) {
    markSeatActive(existing, now);
    if (command.language) existing.language = command.language;
    return { state };
  }
  if (state.phase === 'finale') return fail('wrong-phase');
  if (state.seats.length >= COOP_MAX_SEATS) return fail('seats-full');
  if (state.seats.some((seat) => seat.sliceIndex === command.sliceIndex)) return fail('slice-taken');
  // Anyone may join at any time; a late player votes from their first tap.
  state.seats.push({
    seatId: command.seatId,
    sliceIndex: command.sliceIndex,
    lens: defaultLens(command.sliceIndex),
    language: command.language ?? 'en',
    lastActiveAt: now,
    away: false,
    lobbyReady: state.phase !== 'lobby',
    continued: false,
    ready: false,
    ballot: []
  });
  return { state };
}

export interface CoopOutcome {
  path: CoopPath;
  stars: number;
  warmingC: number;
}

export function coopOutcome(state: CoopGameState): CoopOutcome {
  return { path: pathFor(state.planet.warmingC), stars: starsFor(state.planet), warmingC: state.planet.warmingC };
}

/** The card the team backed most over the game (ties: content order), or null. */
export function coopMostBackedCard(totals: CoopTeamTotals): string | null {
  let best: string | null = null;
  for (const [id, count] of Object.entries(totals.votesByCard)) {
    const current = best === null ? -1 : totals.votesByCard[best] ?? 0;
    if (count > current || (count === current && best !== null && (CARD_ORDER.get(id) ?? 0) < (CARD_ORDER.get(best) ?? 0))) best = id;
  }
  return best;
}
